import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useUser } from "../../export";
import { Learner } from "./components/types";
import { useAuth } from "../../../auth/export";
import { useSession } from "../../../session/export";
import Button from "../../../../grapes/atoms/button/Button";
import DisplayLearners from "./components/DisplayLearners";
import AddLearnersModal from "./components/AddLearnersModal";
import { GuideAPI } from "../../guide-api";
import PageLoadingDiv from "../../../../grapes/atoms/pageLoading";
import GuideHeroCard from "./components/GuideHeroCard";
import mixpanel from "mixpanel-browser";
import DisplayStartProject from "./components/DisplayStartProject";
import StartProjectModal from "./components/StartProjectModal";

const GuideDashboard = () => {
    const user = useUser();
    const auth = useAuth();
    const session = useSession();
    const navigate = useNavigate();
    const [isAddLearnersModalOpen, setIsAddLearnersModalOpen] = useState(false);
    const [isStartProjectModalOpen, setIsStartProjectModalOpen] = useState(false);
    const [learners, setLearners] = useState<Learner[]>([]);
    const [learnersAdded, setLearnersAdded] = useState<Learner[]>([]);

    useEffect(() => {
        const fetchLearners = async () => {
        try {
            const response = await GuideAPI.fetchLearners({ email: auth.state.data.email });
            setLearners(response.data);
        } catch (error) {
            logger.error("Error fetching learners:", error);
        }
        };

        fetchLearners();
    }, [auth.state.data.email]);

    useEffect(() => {
        const checkLearners = async () => {
            try {
                const learners = await GuideAPI.fetchLearners({ email: auth.state.data.email });
                if (!learners || learners.data.length === 0) {
                    setIsAddLearnersModalOpen(true);
                }
            } catch (error) {
                logger.log("Error fetching learners:", error);
            }
        };

        checkLearners();
    }, [auth.state.data.email]);

    useEffect(() => {
        logger.log("isStartProjectModalOpen updated:", isStartProjectModalOpen);
    }, [isStartProjectModalOpen]);

    return (
        <>
            <div className="flex h-full max-h-screen flex-row">
                <div className="h-full min-h-screen flex w-full flex-row">
                    {session.loading ? (
                        <div className="h-screen flex flex-row">
                            <PageLoadingDiv />
                        </div>
                    ) : (
                        <div className="flex min-h-screen h-full w-full flex-col overflow-hidden relative">
                            <div className="min-h-screen h-full flex w-full flex-col space-y-4">
                                <div>
                                    <GuideHeroCard 
                                        heroText={`Welcome ${user.state.data.name}`} 
                                        buttonInfo={{
                                            onClick: () => {
                                                navigate("/");
                                                if (process.env.REACT_APP_MIXPANEL_TOKEN) {
                                                    mixpanel.track('Guide using Learner View', { email: auth.state.data.email });
                                                }
                                            },
                                            text: "Go to learner view"
                                        }}
                                    />
                                </div>
                                <div>
                                    <div className="flex flex-col w-full sm:flex-row justify-start sm:justify-between items-start sm:items-center space-y-4 sm:space-y-0 mt-4 px-8">
                                        <h3 className="font-bold w-full text-black dark:text-white">
                                            Your Learners
                                        </h3>
                                        <div className="flex w-full items-center gap-4 justify-end">
                                            <Button
                                                variant="primary"
                                                label="Add Learners"
                                                onClick={() => {
                                                    setIsAddLearnersModalOpen(!isAddLearnersModalOpen);
                                                }}
                                            >
                                                <div className="flex flex-row w-full items-center justify-center ml-1 gap-2">
                                                    <FontAwesomeIcon icon={faPlus} />
                                                    <p className="w-full">Add Learners</p>
                                                </div>
                                            </Button>
                                        </div>
                                    </div>
                                    <hr className="border-t-2 px-4 mt-8 border-pf-dark-grey dark:border-white"></hr>
                                </div>
                                <div className="flex flex-row h-full w-full flex-grow">
                                    <div className="w-3/4 pr-4 overflow-y-auto">
                                        <DisplayLearners learnersAdded={learnersAdded} />
                                    </div>
                                    <div className="w-1/4 pl-4 flex items-center justify-center">
                                        <DisplayStartProject onClick={() => setIsStartProjectModalOpen(!isStartProjectModalOpen)} />
                                    </div>
                                </div>
                            </div>
                            <AddLearnersModal
                                isOpen={isAddLearnersModalOpen}
                                onClose={() => setIsAddLearnersModalOpen(false)}
                                userEmail={auth.state.data.email}
                            />
                            <StartProjectModal
                                isOpen={isStartProjectModalOpen}
                                onClose={() => setIsStartProjectModalOpen(false)}
                                userEmail={auth.state.data.email}
                                learners={learners}
                                setLearnersAdded={setLearnersAdded}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default GuideDashboard;